import React, { useState, useEffect } from 'react';
import { useForm } from "react-hook-form";
import { startOfDay, isValid } from "date-fns";
import API from "../http/API";
import { useCurrentUser, useAuthToken, useRequestUrl } from "../context/contextHooks";
import {useSxStyles as sx, useSxStyles as classes} from "../styles/sxStyles";
import { validateOutcomes, validateReport, getReportRequest } from "./callLoggingFunctions";
import Grid2 from "@mui/material/Grid2";
import Paper from '@mui/material/Paper';
import Typography from "@mui/material/Typography";
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {DatePicker} from "@mui/x-date-pickers";
import Select from '@mui/material/Select';
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from '@mui/material/Box';
import Button from "@mui/material/Button";
import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
import EditOutcomes from "./EditOutcomes";
import TableViewTwoToneIcon from '@mui/icons-material/TableViewTwoTone';
import CallLog from "./CallLog";
import UpdateUserPassword from "../users/UpdateUserPassword";

const ManageLogging = () => {
    const [currentUser,] = useCurrentUser();
    const [authToken,] = useAuthToken();
    const [requestUrl,] = useRequestUrl();
    const { register, handleSubmit } = useForm();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [message, setMessage] = useState(null);
    const [actionResponse, setActionResponse] = useState(null);
    const [usersData, setUsersData] = useState(null);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [editOutcomes, setEditOutcomes] = useState(false);
    const [outcomes, setOutcomes] = useState(null);
    const [openOutcomes, setOpenOutcomes] = useState(false);
    const [logEntries, setLogEntries] = useState(false);

    const clearMessages = () => {
        setErrorMessage(null);
        setActionResponse(null);
    }

    const retrieveUsers = (agencyId) => {
        if (!loading) {
            clearMessages();
            setMessage(null);
            setLoading(true);
            const url = `${requestUrl}/users`;
            const request = `{"agencyId": "${agencyId}"}`;
            API.postRetrieve(url, request, 'users', `Bearer ${authToken}`, setActionResponse);
        }
    }

    const handleStartDate = (newVal) => {
        if (isValid(newVal)) {
            setStartDate(startOfDay(newVal));
        }
    }

    const handleEndDate = (newVal) => {
        if (isValid(newVal)) {
            setEndDate(startOfDay(newVal));
        }
    }

    const handleEditOutcomes = () => {
        setMessage(null);
        retrieveOutcomes();
    }

    const handleRetrieveEntries = (form) => {
        const errors = validateReport(startDate, endDate);
        if (errors) {
            setErrorMessage(errors);
        } else {
            setOpenOutcomes(false);
            const request = getReportRequest(form, startDate, endDate);
            retrieveLogEntries(JSON.stringify(request));
        }
    }

    const retrieveLogEntries = (request) => {
        clearMessages();
        setLoading(true);
        const url = `${requestUrl}/getLoggingEntries`;
        API.postRetrieve(url, request, 'getEntries', `Bearer ${authToken}`, setActionResponse);
    }

    const retrieveOutcomes = () => {
        clearMessages();
        setLoading(true);
        const url = `${requestUrl}/getOutcomeReasons/${currentUser.agencyId}`;
        API.getRetrieve(url, 'outcomes', `Bearer ${authToken}`, setActionResponse);
    }

    const saveOutcomes = () => {
        const errors = validateOutcomes(outcomes);
        if (errors) {
            setErrorMessage(errors);
        } else {
            clearMessages();
            setMessage(null);
            setLoading(true);
            const url = `${requestUrl}/saveOutcomeReasons/${currentUser.agencyId}`;
            const data = outcomes ? JSON.stringify(outcomes) : '[]';
            API.postRetrieve(url, data, 'saveOutcomes', `Bearer ${authToken}`, setActionResponse);
        }
    }

    useEffect(() => {
        if (actionResponse && loading) {
            if (actionResponse.error) {
                setErrorMessage(actionResponse.errorMessage);
                setLoading(false);
            } else if (actionResponse.requestType === 'users') {
                setUsersData(actionResponse.data);
                setLoading(false);
            } else if (actionResponse.requestType === 'outcomes') {
                setOutcomes(actionResponse.data);
                setEditOutcomes(true);
                setLoading(false);
            } else if (actionResponse.requestType === 'saveOutcomes') {
                setMessage(actionResponse.message);
                setLoading(false);
                retrieveOutcomes();
            } else if (actionResponse.requestType === 'getEntries') {
                setLogEntries(actionResponse.data);
                setLoading(false);
            }
        }
    }, [actionResponse])

    useEffect(() => {
        if (!usersData && !loading) {
            retrieveUsers(currentUser.agencyId);
        }
    }, [usersData])

    useEffect(() => {
        if (editOutcomes && outcomes && !openOutcomes) {
            setOpenOutcomes(true);
        }
        if (!openOutcomes) setMessage(null);
    }, [outcomes, openOutcomes])

    return (<>
        <Typography variant="h4" color='primary' sx={classes.header}>Call Logging</Typography>
        <Grid2
            container
            spacing={2}
            alignItems="top"
            justifyContent="center"
        >
            <Grid2
                size={{
                    xs: 12,
                    xl: 4
                }}>
                <Paper elevation={3} sx={classes.controlPanel}>
                    <Typography sx={classes.inputLabel} variant="h5" color='primary'>Logging Report</Typography>
                    <Grid2 container spacing={1} alignItems='top'>
                        <Grid2
                            size={{
                                xs: 12,
                                md: 6
                            }}>
                            <FormControl
                                name='startDate'
                                component="fieldset"
                                fullWidth
                                sx={classes.formControl}
                            >
                                <FormLabel shrink={true} sx={{...classes.inputLabel, marginBottom: '-.5rem'}}>
                                    Start Date
                                </FormLabel>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        name='startDate'
                                        onChange={handleStartDate}
                                        value={startDate}
                                        format='MM/dd/yyyy'
                                        slotProps={{
                                            textField: {
                                                inputProps: {
                                                    sx: classes.textbox
                                                }
                                            }
                                        }}
                                    />

                                </LocalizationProvider>
                            </FormControl>
                        </Grid2>
                        <Grid2
                            size={{
                                xs: 12,
                                md: 6
                            }}>
                            <FormControl
                                name='endDate'
                                fullWidth
                                sx={classes.formControl}
                            >
                                <FormLabel shrink={true} sx={{...classes.inputLabel, marginBottom: '-.5rem'}}>
                                    End Date
                                </FormLabel>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        name='endDate'
                                        onChange={handleEndDate}
                                        value={endDate}
                                        format='MM/dd/yyyy'
                                        slotProps={{
                                            textField: {
                                                inputProps: {
                                                    sx: classes.textbox
                                                }
                                            }
                                        }}
                                    />

                                </LocalizationProvider>
                            </FormControl>
                        </Grid2>
                        <Grid2
                            size={{
                                xs: 12,
                                md: 6
                            }}>
                            <FormControl
                                name='reportUser'
                                component="fieldset"
                                fullWidth
                                sx={classes.formControl}
                                {...register('reportUser')}
                            >
                                <FormLabel component="legend" sx={classes.inputLabel}>
                                    <Typography variant="subtitle1">
                                        User
                                    </Typography>
                                </FormLabel>
                                <Select
                                    name='reportUser'
                                    native
                                    variant='outlined'
                                    fullWidth
                                    defaultValue='ALL'
                                    sx={classes.select}
                                >
                                    <option key='ALL' value='ALL'>All</option>
                                    { usersData && usersData.length > 0 && usersData.map((user) => {
                                        return (
                                            <option key={user.key} value={user.username}>{`${user.firstName} ${user.lastName}`}</option>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid2>
                    </Grid2>
                </Paper>
                <Box component='span' display='flex' flexDirection="row-reverse">
                    <Button variant="contained"
                            color="primary"
                            startIcon={<ListAltTwoToneIcon/>}
                            sx={sx.button}
                            onClick={handleEditOutcomes}
                    >
                        Outcome Reasons
                    </Button>
                    <Button variant="contained"
                            color="primary"
                            startIcon={<TableViewTwoToneIcon/>}
                            sx={sx.button}
                            onClick={handleSubmit(handleRetrieveEntries)}
                    >
                        Retrieve Log Entries
                    </Button>
                </Box>
            </Grid2>
            <Grid2
                sx={{paddingTop: '1rem'}}
                size={{
                    xs: 12,
                    xl: 8
                }}>
                { errorMessage &&
                    <Alert severity="error" sx={{marginBottom: '.5rem'}}>
                        <AlertTitle>Error</AlertTitle>
                        {errorMessage}
                    </Alert>
                }
                { message &&
                    <Alert severity='success' sx={{marginBottom: '.5rem'}}>
                        {message}
                    </Alert>
                }
                { openOutcomes &&
                    <EditOutcomes
                        outcomes={outcomes}
                        setOutcomes={setOutcomes}
                        setOpenOutcomes={setOpenOutcomes}
                        saveOutcomes={saveOutcomes}
                    />
                }
                { logEntries &&
                    <CallLog entries={logEntries} setEntries={setLogEntries} />
                }
            </Grid2>
        </Grid2>
        <UpdateUserPassword />
    </>);
}

export default ManageLogging;
